import { useEffect, useRef, useState } from 'react';

import { arrangeInTableOrder } from 'components/SaloonLobby/tableOrder';

// A grid "slot" is a stable cell identity that survives the table it shows
// changing. Cells are keyed by `slotId` (stable) while the per-table subtree
// inside is keyed by `gameID` — so an MTT balancing move re-points the cell to
// the new table IN PLACE (no remove+add, no grid reflow), the same "persistent
// surface" the single-table view uses.
export type GridSlot = { slotId: number; gameID: string };

export type GridRebind = { from: string; to: string } | null;

/**
 * Reconcile the current slot list against the live set of open tables
 * (`gameIDs`, from state.tables) plus a pending balancing rebind. Pure so the
 * behaviour is unit-testable.
 *
 * Rules, in order:
 *  1. Rebind in place, once there is something to show: the slot showing
 *     `rebind.from` becomes `rebind.to` when `to` has loaded, or when `from` is
 *     no longer open (the balancing handler's teardown after its 3 s wait),
 *     whichever comes first. Until then the cell keeps showing the source —
 *     the table the player is still seated at, under its "being moved"
 *     screen — instead of the destination's empty, unloaded felt (EN-10625,
 *     Marlon on test4: "it glitches to an empty table while balancing").
 *     Keeping the slotId (React key) stable means the cell box stays put (no
 *     reflow) while the inner subtree (keyed on gameID) swaps. If the source is
 *     torn down before the destination lands, the cell shows the destination
 *     in a loading state until its data arrives, as before.
 *  2. Keep slots for open tables PLUS the pending rebind destination while its
 *     first broadcast is still in flight (so the just-rebound cell isn't dropped
 *     before its table lands).
 *  3. Append newly-opened tables (in `gameIDs` order) that aren't slotted yet,
 *     skipping the rebind SOURCE if it still lingers in state.tables the same
 *     tick (it's absorbed by the rebound slot, not a new cell).
 */
export const reconcileSlots = (
  prev: GridSlot[],
  gameIDs: string[],
  rebind: GridRebind,
  allocId: () => number
): GridSlot[] => {
  const present = new Set(gameIDs);
  let next = prev;

  // 1) Apply the rebind once `to` has loaded or `from` has closed; until then
  //    the source cell stays as it is. Two shapes:
  //      a. Destination is NOT already a slot → reassign the source slot's
  //         gameID in place (keeps the same slotId / React key so the cell
  //         doesn't remount, no reflow). Standard MTT balancing case.
  //      b. Destination IS already a slot → drop the source slot outright.
  //         This happens when App.tsx's gotPlayerGames auto-resub subscribes
  //         the balanced-to gameID BEFORE our balancing effect fires (a race
  //         in re-entry / rebalance during break). The player only needs one
  //         cell for the destination; without this branch, the pre-existing
  //         destination slot stayed alongside the orphaned source slot,
  //         creating the "empty duplicate cell" the user reported.
  const holdingSource = rebind !== null && present.has(rebind.from) && !present.has(rebind.to);
  if (rebind && !holdingSource) {
    const idx = next.findIndex((s) => s.gameID === rebind.from);
    if (idx !== -1) {
      const destAlreadySlot = next.some((s, i) => i !== idx && s.gameID === rebind.to);
      if (destAlreadySlot) {
        next = next.filter((_, i) => i !== idx);
      } else {
        next = next.map((s, i) => (i === idx ? { ...s, gameID: rebind.to } : s));
      }
    }
  }

  // 2) Keep open tables + the pending rebind destination (loading in flight).
  const pendingTo = rebind ? rebind.to : null;
  const kept = next.filter((s) => present.has(s.gameID) || s.gameID === pendingTo);

  // 3) Append new tables not yet represented by a slot; never re-add the rebind
  //    source (it's been absorbed by the rebound slot).
  const consumedFrom = rebind ? rebind.from : null;
  const slotted = new Set(kept.map((s) => s.gameID));
  const added: GridSlot[] = [];
  for (const gameID of gameIDs) {
    if (!slotted.has(gameID) && gameID !== consumedFrom) {
      added.push({ slotId: allocId(), gameID });
      slotted.add(gameID);
    }
  }

  const result = added.length ? [...kept, ...added] : kept;
  // Preserve the previous reference when nothing changed (avoids render churn).
  if (
    result.length === prev.length &&
    result.every((s, i) => prev[i] && prev[i].slotId === s.slotId && prev[i].gameID === s.gameID)
  ) {
    return prev;
  }
  return result;
};

/**
 * Decide which table should be the SELECTED grid cell, given the current cell
 * order, the current selection, and any pending balancing rebind. Returns the
 * gameID to select, or null to leave the selection unchanged. Pure/ testable.
 *
 *  - Selection still valid → null (keep it).
 *  - Selection is the source of a balancing rebind → FOLLOW it to the
 *    destination once that cell exists (so focus stays on the player's table as
 *    it's re-pointed); until then, null (don't snap away mid-move).
 *  - Otherwise (selection genuinely gone, e.g. a closed table) → first cell.
 */
export const resolveGridSelection = (
  orderedGameIDs: string[],
  selected: string | null,
  rebind: GridRebind
): string | null => {
  if (orderedGameIDs.length === 0) return null;
  if (selected && orderedGameIDs.includes(selected)) return null;
  if (rebind && selected === rebind.from) {
    return orderedGameIDs.includes(rebind.to) ? rebind.to : null;
  }
  return orderedGameIDs[0];
};

/**
 * Where a pending slot goes in `out`: just before the nearest slot that was to
 * its right last render, or just after the nearest one that was to its left —
 * neighbours, not the old absolute index, so a table closing beside it can't
 * move it past another cell. Front when it had no previous position.
 */
const pendingInsertIndex = (slotId: number, out: GridSlot[], previousSlotIds: number[]): number => {
  const at = previousSlotIds.indexOf(slotId);
  if (at === -1) return 0;
  const indexIn = (id: number) => out.findIndex((s) => s.slotId === id);
  for (let i = at + 1; i < previousSlotIds.length; i++) {
    const right = indexIn(previousSlotIds[i]);
    if (right !== -1) return right;
  }
  for (let i = at - 1; i >= 0; i--) {
    const left = indexIn(previousSlotIds[i]);
    if (left !== -1) return left + 1;
  }
  return 0;
};

/**
 * Put the slots in table order (`order`: open gameIDs, leftmost first). A slot
 * whose table isn't open yet — the pending rebind destination, the only one
 * reconcileSlots keeps — stays where its cell already was (`previousSlotIds`)
 * instead of jumping to the front until its table loads (EN-10857).
 */
export const orderGridSlots = (
  slots: GridSlot[],
  order: string[],
  previousSlotIds: number[]
): GridSlot[] => {
  const pending = slots.filter((s) => !order.includes(s.gameID));
  if (pending.length === 0) return arrangeInTableOrder(slots, order, (s) => s.gameID);
  const placed = arrangeInTableOrder(
    slots.filter((s) => order.includes(s.gameID)),
    order,
    (s) => s.gameID
  );
  const out = [...placed];
  for (const slot of pending) {
    out.splice(pendingInsertIndex(slot.slotId, out, previousSlotIds), 0, slot);
  }
  return out;
};

/**
 * Stateful wrapper around reconcileSlots. Returns the ordered slots for the
 * grid and clears a pending rebind (via `onRebindConsumed`) once its
 * destination table has arrived and been applied.
 */
export const useGridSlots = (
  gameIDs: string[],
  rebind: GridRebind,
  onRebindConsumed: () => void
): GridSlot[] => {
  const [slots, setSlots] = useState<GridSlot[]>(() =>
    gameIDs.map((gameID, i) => ({ slotId: i + 1, gameID }))
  );
  const nextIdRef = useRef(gameIDs.length + 1);
  const gameIDsKey = gameIDs.join(',');
  const rebindKey = rebind ? `${rebind.from}>${rebind.to}` : '';

  useEffect(() => {
    setSlots((prev) => reconcileSlots(prev, gameIDs, rebind, () => nextIdRef.current++));
    // The destination has landed → the rebind is applied (or moot); clear it so
    // the next open of the same source table isn't treated as a rebind.
    if (rebind && gameIDs.includes(rebind.to)) onRebindConsumed();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameIDsKey, rebindKey]);

  return slots;
};
